import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import { objectKeysHasVal } from "components/ProjectNonSpecificComponents/Functions/objectKeysHasVal"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import getProjectIdFromConfiguredCollections from "components/ProjectSpecificComponents/Functions/Data/getProjectIdFromConfiguredCollections"
import {
	isReactSelectOptionArray,
	isReactSelectSingleOption,
	TReactSelectOption,
} from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import { IItemOfTypeLevels } from "components/ProjectSpecificComponents/Interfaces/FormsInterface"
import { observer } from "mobx-react-lite"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import InfoBubble from "../../Global/atoms/InfoBubble"
import Select from "../../Global/atoms/Select"
import { USER_REQ_CLASSNAME } from "../organisms/UserSubmitRequest"
import getIOTLevels, { TItemOfTypeLevels } from "./UserSideGetIOTLevels"

interface Props {
	valueKey: string | undefined
	value: readonly TReactSelectOption[] | undefined
	picklistValue: string | undefined
	options: TReactSelectOption[]
	isMultiSelect: boolean
	onChangeValue: (value: string | readonly TReactSelectOption[] | TReactSelectOption, customKey?: string) => void
	mainFieldKey: string
	itemOfTypeLevels: IItemOfTypeLevels
	name: string
	tooltipText?: string
	isRequired?: boolean
}

/**
 * Item of type can be a single select, or it can be as long as three selects.
 * Each select filters values for the next select
 */
const UserSideNewRequestItemOfType = observer(
	({
		valueKey,
		picklistValue,
		value,
		options,
		isMultiSelect,
		onChangeValue,
		mainFieldKey,
		itemOfTypeLevels: {
			itemOfTypeId,
			isPicklistFirstSetSecond,
			levelOneEnabled,
			levelTwoEnabled,
			firstLevelFieldNameLV,
			firstLevelFieldNameEN,
			secondLevelFieldNameLV,
			secondLevelFieldNameEN,
			picklistFieldId,
			stringFieldId,
		},
		name,
		tooltipText,
		isRequired = false,
	}: Props) => {
		const formStore = useFormStore()
		const store = useStore()
		const { isMobile } = useScreenType()
		const [loadingData, setLoadingData] = useState<boolean>(false)
		const { i18n } = useTranslation()
		const isEN = i18n.language === "en"
		
		const projectId: string = useMemo(() => {
			const { userSelectedForm } = formStore
			if (userSelectedForm == null) return ""
            
			const projectId = getProjectIdFromConfiguredCollections(
				userSelectedForm?.collections,
				picklistValue
			)

			return projectId ?? ""
		}, [picklistValue])

		const { frstLvlOptions, scndLvlOptions, thrdLvlOptions, frstLvlValue, scndLvlValue } =
			formStore.userFormItemOfTypeLevels

		useEffect(() => {
			formStore.resetUserFormItemOfTyleLevels()

			const searchItemOfTypeLevels = async () => {
				setLoadingData(true)
				const params: TItemOfTypeLevels = {
					itemOfTypeItemId: itemOfTypeId,
					isOneLevelOnly: !levelTwoEnabled,
					picklistFieldId,
					projectId,
					isPicklistFirstSetSecond,
				}
				if (objectKeysHasVal(params)) {
					const frstLvlOptions = await getIOTLevels(params)
					formStore.setUserFormItemOfTypeLevels("frstLvlOptions", frstLvlOptions)
				}

				setLoadingData(false)
			}

			searchItemOfTypeLevels()
		}, [projectId])

		return (
			<>
				{levelOneEnabled && (
					<HyperDiv direction={isMobile ? "column" : "row"} width="100%" spacedEvenly>
						<p className="w-full">{isEN ? firstLevelFieldNameEN : firstLevelFieldNameLV}</p>
						<div className="w-full flex flex-row items-end">
							<Select
								className={isRequired ? USER_REQ_CLASSNAME : ""}
								valueKey={frstLvlValue}
								options={frstLvlOptions}
								isMulti={false}
								isLoading={loadingData}
								onChange={(selectedOptions) => {
									// On change clear second and third value
									formStore.setUserFormItemOfTypeLevels("scndLvlValue", undefined)
									onChangeValue("")
									onChangeValue("", "USER_SETTABLE")
									formStore.setUserFormItemOfTypeLevels("thrdLvlOptions", undefined)

									if (isReactSelectSingleOption(selectedOptions)) {
										// For Single pick-list
										if (isPicklistFirstSetSecond) {
											onChangeValue(selectedOptions, picklistFieldId)
										} else {
											// set sends label
											onChangeValue(selectedOptions.label, stringFieldId)
										}

										formStore.setUserFormItemOfTypeLevels("frstLvlValue", selectedOptions.value)
										formStore.setUserFormItemOfTypeLevels(
											"scndLvlOptions",
											selectedOptions.extraProp
										)
									} else {
										if (isPicklistFirstSetSecond) {
											onChangeValue("", picklistFieldId)
										} else {
											onChangeValue("", stringFieldId)
										}

										// To clear field if no value is selected
										formStore.setUserFormItemOfTypeLevels("scndLvlOptions", undefined)
										formStore.setUserFormItemOfTypeLevels("frstLvlValue", undefined)
									}
								}}
							/>
							{tooltipText && (
								<div className="pb-2.5 pl-2">
									<InfoBubble tooltipText={tooltipText} />
								</div>
							)}
						</div>
					</HyperDiv>
				)}

				{scndLvlOptions && levelTwoEnabled && (
					<HyperDiv
						direction={isMobile ? "column" : "row"}
						padding={levelOneEnabled ? "2rem 0px 0px 0px" : ""}
						width="100%"
						spacedEvenly
					>
						<p className="w-full">{isEN ? secondLevelFieldNameEN : secondLevelFieldNameLV}</p>

						<div className="w-full flex flex-row items-end">
							<Select
								className={isRequired ? USER_REQ_CLASSNAME : ""}
								valueKey={scndLvlValue}
								options={scndLvlOptions}
								isMulti={false}
								onChange={(selectedOptions) => {
									// On change clear third value
									onChangeValue("")
									onChangeValue("", "USER_SETTABLE")

									if (isReactSelectSingleOption(selectedOptions)) {
										if (!isPicklistFirstSetSecond) {
											onChangeValue(selectedOptions, picklistFieldId)
										} else {
											// set sends label
											onChangeValue(selectedOptions.label, stringFieldId)
										}
										formStore.setUserFormItemOfTypeLevels("scndLvlValue", selectedOptions.value)
										formStore.setUserFormItemOfTypeLevels(
											"thrdLvlOptions",
											selectedOptions.extraProp
										)
									} else {
										if (!isPicklistFirstSetSecond) {
											onChangeValue("", picklistFieldId)
										} else {
											onChangeValue("", stringFieldId)
										}
										formStore.setUserFormItemOfTypeLevels("scndLvlValue", undefined)
										formStore.setUserFormItemOfTypeLevels("thrdLvlOptions", undefined)
									}
								}}
							/>
						</div>
					</HyperDiv>
				)}

				{/* If is not 2/3  */}

				{((levelOneEnabled && levelTwoEnabled && thrdLvlOptions) ||
					(levelOneEnabled && !levelTwoEnabled && scndLvlOptions) ||
					!levelOneEnabled) && (
					<HyperDiv
						direction={isMobile ? "column" : "row"}
						padding={
							levelOneEnabled || (scndLvlOptions && levelTwoEnabled) ? "2rem 0px 0px 0px" : ""
						}
						width="100%"
						spacedEvenly
					>
						<p className="w-full">{name}</p>
						<div className="w-full flex flex-row items-end">
							<Select
								className={isRequired ? USER_REQ_CLASSNAME : ""}
								valueKey={valueKey}
								value={levelOneEnabled ? undefined : value}
								options={
									levelTwoEnabled ? thrdLvlOptions : levelOneEnabled ? scndLvlOptions : options
								}
								isMulti={levelOneEnabled ? false : isMultiSelect}
								onChange={(selectedOptions) => {
									if (isReactSelectSingleOption(selectedOptions)) {
										// For Single pick-list
										onChangeValue(selectedOptions)
									} else if (isReactSelectOptionArray(selectedOptions)) {
										// For Multi pick-list
										onChangeValue(selectedOptions)
									} else {
										// To clear field if no value is selected
										onChangeValue("")
									    onChangeValue("", "USER_SETTABLE")
									}
								}}
							/>

							{tooltipText && !levelOneEnabled && (
								<div className="pb-2.5 pl-2">
									<InfoBubble tooltipText={tooltipText} />
								</div>
							)}
						</div>
					</HyperDiv>
				)}
			</>
		)
	}
)

export default UserSideNewRequestItemOfType
